import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Zap, Crown, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { getAuthData } from '../utils/auth';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';

const PricingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const { user } = getAuthData();

  const fetchData = useCallback(async () => {
    try {
      const [tiersRes, subRes] = await Promise.all([
        api.get('/pricing/tiers'),
        user ? api.get('/user/subscription') : Promise.resolve(null)
      ]);
      setTiers(tiersRes.data.tiers);
      if (subRes) {
        setCurrentSubscription(subRes.data);
      }
    } catch (error) {
      toast.error('Error loading pricing');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out');
      return;
    }

    try {
      const response = await api.get(`/payment/status/${sessionId}`);
      
      if (response.data.paymentStatus === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      toast.error('Error checking payment status');
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [fetchData, pollPaymentStatus, searchParams]);

  const handleUpgrade = async (tierKey) => {
    if (!user) {
      navigate('/register');
      return;
    }

    setUpgrading(tierKey);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/payment/checkout', {
        tier: tierKey,
        originUrl
      });
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating checkout');
      setUpgrading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  const tierIcons = {
    free: <Zap className="h-8 w-8" />,
    pro: <Rocket className="h-8 w-8" />,
    enterprise: <Crown className="h-8 w-8" />
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm">
        <div className="container mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="text-2xl font-heading font-bold text-primary">cyQRus</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {user && (
              <Link to="/dashboard">
                <Button variant="ghost">Dashboard</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-primary mb-4" data-testid="pricing-title">
            Choose Your Plan
          </h1>
          <p className="text-lg text-muted-foreground">
            Start free, upgrade as you grow. All plans include custom branding.
          </p>
          {currentSubscription && (
            <div className="mt-4 inline-block px-4 py-2 bg-accent/10 rounded-lg">
              <p className="text-sm">
                Current Plan: <span className="font-semibold">{currentSubscription.tierInfo.name}</span>
                {' '}({currentSubscription.usage.qrCodes}/{currentSubscription.usage.qrLimit === -1 ? '∞' : currentSubscription.usage.qrLimit} QR codes used)
              </p>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {Object.entries(tiers).map(([key, tier]) => (
            <div
              key={key}
              className={`card p-8 ${
                key === 'pro' ? 'ring-2 ring-accent scale-105' : ''
              }`}
              data-testid={`pricing-tier-${key}`}
            >
              {key === 'pro' && (
                <div className="bg-accent text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                  MOST POPULAR
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-lg ${
                  key === 'free' ? 'bg-primary/10 text-primary' :
                  key === 'pro' ? 'bg-accent/10 text-accent' :
                  'bg-amber-500/10 text-amber-600'
                }`}>
                  {tierIcons[key]}
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-primary">{tier.name}</h3>
                </div>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-heading font-bold text-primary">${tier.price}</span>
                <span className="text-muted-foreground">/month</span>
              </div>

              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {key === 'free' ? (
                user && currentSubscription?.currentTier === 'free' ? (
                  <Button className="w-full" variant="outline" disabled>
                    Current Plan
                  </Button>
                ) : (
                  <Link to="/register" className="block">
                    <Button className="w-full" variant="outline">
                      Get Started
                    </Button>
                  </Link>
                )
              ) : (
                <Button
                  className="w-full btn-accent"
                  onClick={() => handleUpgrade(key)}
                  disabled={upgrading === key || (currentSubscription?.currentTier === key)}
                  data-testid={`upgrade-to-${key}-btn`}
                >
                  {upgrading === key ? 'Processing...' :
                   currentSubscription?.currentTier === key ? 'Current Plan' : 'Upgrade Now'}
                </Button>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
